/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { useCallback, useMemo, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { isMobile } from 'react-device-detect';
import { OverlayTrigger, Tooltip, Form, Spinner } from 'react-bootstrap';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Icon from '_components/atomic/Icon';

const { colors, fontSizes, spaces } = theme;

const StyledGroup = styled(
  forwardRef(({ labeled, noBottomSpace, ...props }, ref) => (
    <Form.Group ref={ref} {...props} />
  ))
)`
  position: relative;
  padding-top: ${({ labeled }) => (labeled ? spaces.medium : 0)}px;
  margin-bottom: ${spaces.regular}px;

  ${({ noBottomSpace }) =>
    noBottomSpace &&
    css`
      margin-bottom: 0;
    `}

  @media ${theme.devices.tablet} {
    padding-top: ${({ labeled }) => (labeled ? spaces.large : 0)}px;
  }
`;

const StyledLabel = styled(
  forwardRef(({ invalid, ...props }, ref) => (
    <Form.Label ref={ref} {...props} />
  ))
)`
  position: absolute;
  top: 0;
  left: ${spaces.smallest}px;
  margin: 0;
  font-family: 'Aeonik';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${colors.gray[800]};

  span {
    color: ${colors.indigo[600]};
  }

  ${({ invalid }) =>
    invalid &&
    css`
      color: ${colors.indigo[600]};
    `}

  @media ${theme.devices.tablet} {
    font-size: ${fontSizes.small}px;
  }
`;

const StyledControl = styled(
  forwardRef(({ processing, ...props }, ref) => (
    <Form.Control ref={ref} {...props} />
  ))
)`
  min-height: 26px;

  padding-top: ${spaces.small2X}px;
  padding-bottom: ${spaces.small2X}px;
  padding-left: ${spaces.small}px;
  padding-right: ${spaces.small}px;

  border: 1px solid ${colors.gray[200]};
  border-radius: 26px;
  background-color: ${colors.white};

  font-family: 'Aeonik';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  color: ${colors.gray[800]};
  caret-color: ${colors.indigo[500]};

  &::placeholder {
    color: ${colors.gray[500]};
  }

  &:focus {
    border-color: ${colors.indigo[500]};
    box-shadow: none;
  }

  &:disabled {
    background-color: ${colors.gray[100]};
  }

  &.form-control.is-valid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.gray[200]};
    background-image: none;

    &:focus {
      border-color: ${colors.indigo[500]};
      box-shadow: none;
    }
  }

  &.form-control.is-invalid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.indigo[600]};
    background-image: none;

    &:focus {
      border-color: ${colors.indigo[500]};
      box-shadow: none;
    }
  }

  ${({ processing }) =>
    processing &&
    css`
      padding-right: ${spaces.large6X}px;
    `};

  &::-webkit-file-upload-button {
    display: none;
  }

  &::-webkit-autofill,
  &::-webkit-contacts-auto-fill-button,
  &::-webkit-credentials-auto-fill-button {
    visibility: hidden;
  }

  @media ${theme.devices.tablet} {
    min-height: 36px;
    padding-top: ${spaces.smallX}px;
    line-height: 128%;
    padding-bottom: ${spaces.smallX}px;
    font-size: ${fontSizes.small}px;
  }
`;

const StyledSpinner = styled(({ labeled, ...props }) => <Spinner {...props} />)`
  position: absolute;
  height: 22px;
  width: 22px;
  top: ${spaces.medium + (26 - 22) / 2}px;
  right: ${spaces.smallest}px;

  &.text-custom {
    color: ${colors.gray[500]};
  }

  ${({ labeled }) =>
    !labeled &&
    css`
      top: ${(26 - 22) / 2}px;
    `}

  @media ${theme.devices.tablet} {
    height: 32px;
    width: 32px;
    top: ${spaces.large + (36 - 32) / 2}px;

    ${({ labeled }) =>
      !labeled &&
      css`
        top: ${(36 - 32) / 2}px;
      `}
  }
`;

const StyledIconContainer = styled(
  forwardRef(({ labeled, ...props }, ref) => <div ref={ref} {...props} />)
)`
  display: flex;
  position: absolute;
  top: ${spaces.medium + (26 - 18) / 2}px;
  right: ${spaces.smallX}px;

  ${({ labeled }) =>
    !labeled &&
    css`
      top: ${(26 - 18) / 2}px;
    `}

  @media ${theme.devices.tablet} {
    top: ${spaces.medium + (36 - 18) / 2}px;

    ${({ labeled }) =>
      !labeled &&
      css`
        top: ${(36 - 18) / 2}px;
      `}
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  padding-left: ${spaces.smallest}px;

  font-family: 'Aeonik';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;

  &.processing-feedback {
    color: ${colors.gray[500]};
  }

  &.valid-feedback {
    color: ${colors.gray[500]};
  }

  &.invalid-feedback {
    color: ${colors.indigo[600]};
  }

  @media ${theme.devices.tablet} {
    font-size: ${fontSizes.small}px;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const BaseInput = forwardRef(
  (
    {
      name,
      type,
      accept,
      hint,
      label,
      loading,
      labelSpace,
      required,
      noBottomSpace,
      valid,
      invalid,
      feedback,
      value,
      onChange,
      ...restProps
    },
    ref
  ) => {
    const handleChangeFile = useCallback(
      (e) => {
        const { files } = e.target;
        onChange &&
          onChange({
            target: { name, value: files?.length ? files[0] : null },
          });
      },
      [name, onChange]
    );

    const overlayTriggerProps = useMemo(() => {
      const props = {
        placement: isMobile ? 'top' : 'right-start',
        delay: { show: 0, hide: 0 },
        trigger: ['hover', 'focus'],
        overlay: (
          <Tooltip id={`tooltip-${name}`} className="hintTooltip">
            {hint}
          </Tooltip>
        ),
      };

      if (!hint || isMobile) {
        props.show = false;
      }

      return props;
    }, [name, hint]);

    return (
      <OverlayTrigger {...overlayTriggerProps}>
        <StyledGroup
          labeled={Boolean(label || labelSpace)}
          noBottomSpace={noBottomSpace}
          className={invalid ? 'custom-control-invalid' : ''}
        >
          {label && (
            <StyledLabel invalid={invalid}>
              {label}
              {label && required && <span>&nbsp;*</span>}
            </StyledLabel>
          )}

          <StyledControl
            {...restProps}
            ref={ref}
            type={type}
            accept={accept}
            isValid={valid}
            isInvalid={invalid}
            processing={loading}
            value={type === 'file' ? undefined : value || ''}
            onChange={type === 'file' ? handleChangeFile : onChange}
            data-hj-allow
          />

          {loading && (
            <>
              <StyledSpinner
                animation="border"
                variant="custom"
                labeled={Boolean(label || labelSpace)}
              />
              {feedback && (
                <StyledFeedback type="processing">{feedback}</StyledFeedback>
              )}
            </>
          )}

          {type === 'file' && value?.name && (
            <StyledFeedback type="processing">{value.name}</StyledFeedback>
          )}

          {!loading && valid && feedback && (
            <StyledFeedback type="valid">
              <Icon
                alias={ICONS_ALIASES.warning}
                color={theme.colors.indigo[600]}
              />
              {feedback}
            </StyledFeedback>
          )}

          {!loading && invalid && feedback && (
            <StyledFeedback type="invalid">
              <Icon
                alias={ICONS_ALIASES.warning}
                color={theme.colors.indigo[600]}
              />
              {feedback}
            </StyledFeedback>
          )}

          {!loading && !valid && !invalid && type === 'file' && (
            <StyledIconContainer labeled={Boolean(label || labelSpace)}>
              <Icon alias={ICONS_ALIASES.link} color={colors.gray[500]} />
            </StyledIconContainer>
          )}
        </StyledGroup>
      </OverlayTrigger>
    );
  }
);

BaseInput.propTypes = {
  name: PropTypes.string,
  type: PropTypes.string,
  accept: PropTypes.string,
  hint: PropTypes.node,
  label: PropTypes.string,
  labelSpace: PropTypes.bool,
  loading: PropTypes.bool,
  required: PropTypes.bool,
  noBottomSpace: PropTypes.bool,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default BaseInput;
